/** @jsxImportSource @emotion/react */
import {
  Typography as MUITypography,
  TypographyProps as MUITypographyProps,
} from '@mui/material';
import DOMPurify from 'dompurify';
import { ElementType, ReactNode } from 'react';
import { useTheme } from '../../ThemeProvider';

type Variant =
  | 'heading1'
  | 'heading2'
  | 'subtitle'
  | 'caption'
  | 'captionMedium'
  | 'captionSmallMedium'
  | 'body'
  | 'bodyMedium'
  | 'bodyBold'
  | 'bodyLarge'
  | 'bodySmall'
  | 'bodySmallMedium'
  | 'bodySmallBold'
  | 'bodySmallest'
  | 'bodySmallestMedium'
  | 'bodySmallestBold';

interface TypographyProps {
  /**
   * Applies the theme typography styles.
   * @default 'body'
   */
  variant?: Variant;

  /**
   * The content of the component.
   */
  children: ReactNode;

  // TODO: Strictly use theme palette colors for foreground!
  color?: string;
  html?: string;
  component?: ElementType;
  dataTestId?: string;
}

const mapToMUIVariant: Record<
  Variant,
  Partial<MUITypographyProps['variant']>
> = {
  heading1: 'h1',
  heading2: 'h2',
  subtitle: 'subtitle1',
  caption: 'caption',
  captionMedium: 'caption',
  captionSmallMedium: 'caption',
  body: 'body1',
  bodyMedium: 'body1',
  bodyBold: 'body1',
  bodyLarge: 'body1',
  bodySmall: 'body1',
  bodySmallMedium: 'body1',
  bodySmallBold: 'body1',
  bodySmallest: 'body1',
  bodySmallestMedium: 'body1',
  bodySmallestBold: 'body1',
};

const getComponent = (variant: Variant) => {
  if (variant === 'heading1') return 'h1';
  if (variant === 'heading2') return 'h2';
  if (variant === 'caption') return 'caption';
  return 'p';
};

export const Typography = ({
  color,
  children,
  variant = 'body',
  component,
  dataTestId,
  html,
}: TypographyProps) => {
  const { typography } = useTheme();

  return (
    <MUITypography
      color={color}
      variant={mapToMUIVariant[variant]}
      component={component ? component : getComponent(variant)}
      css={{
        [`&.MuiTypography-${mapToMUIVariant[variant]}`]: {
          ...typography[variant],
        },
        '&.MuiTypography-root': { marginBottom: 0 },
      }}
      data-testid={dataTestId}
      {...(html
        ? { dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(html) } }
        : undefined)}
    >
      {!html && children ? children : null}
    </MUITypography>
  );
};
